<template>

  <v-data-table
    :headers="headers"
    :items="state.dates"
    :loading="busy"
    items-per-page="-1"
    hide-default-footer
    density="compact"
    class="mb-12 uri-list-table"
    disable-sort
    fixed-header
    height="800"
  >

    <template v-slot:header.total_inventory="{ column }">
      {{ column.title }}
    </template>

    <template
      v-for="lpContext in props.context.lps.values()"
      v-slot:[`header.lp${lpContext.lpId}`]="{ column }"
    >

      {{ column.title }}
      <template v-if="PreferencesService.preferences.showIds">
        (LP {{ lpContext.lpId }})
      </template>

      <v-btn density="compact" class="px-2 ml-2" @click="$emit('editDefaultRates', lpContext.lpId)" v-tooltip:top="$t('actions.edit_default_rates')">

        {{ currencyFormatter.format(lpContext.defaultRate.defaultCost) }}

        <v-overlay activator="parent" location="right" location-strategy="connected" open-on-hover :scrim="false">
          <v-sheet class="border-thin ml-4">
            <v-table density="compact">
              <tbody>
                <tr>
                  <td>{{ $t('titles.default_cost') }}</td>
                  <td class="text-right">{{ currencyFormatter.format(lpContext.defaultRate.defaultCost) }}</td>
                </tr>
                <tr v-for="[channelFacilityId, cdr] in lpContext.defaultRate.channelDefaultRates.entries()" :key="cdr.cdrId">
                  <td>
                    <template v-for="channelFacility in [lpContext.channelFacilities.get(channelFacilityId)]">
                      <template v-if="channelFacility">
                        {{ ChannelRegistry.find(channelFacility.channel)?.title }}
                        ({{ channelFacility.externalFacilityID }})
                      </template>
                      <template v-else>
                        (unknown)
                      </template>
                    </template>
                  </td>
                  <td class="text-right">
                    <template v-if="cdr.channelCost !== lpContext.defaultRate.defaultCost">
                      {{ currencyFormatter.format(cdr.channelCost) }}
                    </template>
                    <template v-if="cdr.channelCost === lpContext.defaultRate.defaultCost">
                      <span class="text-disabled">—</span>
                    </template>
                  </td>
                </tr>
              </tbody>
            </v-table>

          </v-sheet>
        </v-overlay>
      </v-btn>

    </template>

    <template
      v-for="lpContext in props.context.lps.values()"
      v-slot:[`header.lp${lpContext.lpId}.inventory`]="{ column }"
    >
      {{ column.title }}
      <v-btn density="compact" class="px-2 ml-4" @click="$emit('bulkEditInventory', lpContext.lpId)">
        {{ $t('actions.edit') }}
        <v-tooltip activator="parent" location="top center" :text="$t('actions.bulk_edit_inventory')" />
      </v-btn>
    </template>

    <template
      v-for="lpContext in props.context.lps.values()"
      v-slot:[`header.lp${lpContext.lpId}.velocity`]="{ column }"
    >
      {{ column.title }}
    </template>

    <template
      v-for="lpContext in props.context.lps.values()"
      v-slot:[`header.lp${lpContext.lpId}.cost`]="{ column }"
    >
      {{ column.title }}
      <v-btn density="compact" class="px-2 ml-4" @click="$emit('bulkEditCost', lpContext.lpId)">
        {{ $t('actions.edit') }}
        <v-tooltip activator="parent" location="top center" :text="$t('actions.bulk_edit_cost')" />
      </v-btn>
    </template>

    <template v-slot:item.date="{ item }">
      <div class="d-flex justify-space-between w-100">
        <div>{{ item.date.toLocaleString('default', {weekday: 'short'}) }}</div>
        <div class="ml-2">{{ item.date.toLocaleString() }}</div>
      </div>
    </template>

    <template v-slot:item.total_inventory="{ item }">
      <span>
        {{ item.locationSpacesSold === null ? '—' : item.locationSpacesSold }} / {{ context.locationSpacesTotal }}
        (<template v-if="item.locationSpacesRatio !== null">{{ percentageFormatter.format(item.locationSpacesRatio) }}</template><template v-else>—%</template>)
        <v-overlay v-if="Object.keys(item.spacesSoldPerChannel).length" activator="parent" location="bottom" location-strategy="connected" open-on-hover :scrim="false">
          <v-sheet class="border-thin">
            <v-table density="compact">
              <thead>
                <tr>
                  <th scope="col">{{ $t('titles.channel') }}</th>
                  <th scope="col" class="text-right">{{ $t('titles.sold') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="[channel, sold] in Object.entries(item.spacesSoldPerChannel).sort(([channel1, _sold1], [channel2, _sold2]) => channel1 < channel2 ? -1 : 1)" :key="channel">
                  <td>{{ ChannelRegistry.findTitle(channel) }}</td>
                  <td class="text-right">{{ sold }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-sheet>
        </v-overlay>
      </span>
    </template>

    <template v-for="header in lpEntryHeaders.filter(h => h.field === 'inventory')" v-slot:[`item.${header.key}`]="{ item }">

      <template v-for="{lpEntry} of lp(header.lpId, item.date)">

        <o-text-field
          type="number"
          v-model.number="lpEntry.spacesTotal"
          density="compact"
          class="allocation-input"
          hide-details
          :readonly="item.disabled || busy"
          :rules="rules.spacesTotal"
          :append-inner-icon="lpEntry.spacesTotal !== lpEntry.originalSpacesTotal ? '$reset' : undefined"
          @click:appendInner="lpEntry.spacesTotal = lpEntry.originalSpacesTotal"
        >

          <template v-slot:prepend>
            <div class="allocation-input-prepend">
              <span>
                {{ lpEntry.spacesSold }}
                <v-overlay activator="parent" location="left" location-strategy="connected" open-on-hover :scrim="false">
                  <v-sheet class="border-thin">
                    <v-table density="compact">
                      <thead>
                        <tr>
                          <th scope="col">{{ $t('titles.channel') }}</th>
                          <th scope="col" class="text-right">{{ $t('titles.sold') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="[channel, sold] in Object.entries(lpEntry.spacesSoldPerChannel).sort(([channel1, _sold1], [channel2, _sold2]) => channel1 < channel2 ? -1 : 1)" :key="channel">
                          <td>{{ ChannelRegistry.findTitle(channel) }}</td>
                          <td class="text-right">{{ sold }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                  </v-sheet>
                </v-overlay>
              </span>
              <span class="mx-2">/</span>
            </div>
          </template>

          <template v-slot:append>
            <div class="allocation-input-append">
              <template v-if="lpEntry.spacesTotal">
                ({{ percentageFormatter.format(lpEntry.spacesSold / lpEntry.spacesTotal) }})
              </template>
            </div>
          </template>

        </o-text-field>

      </template>

    </template>

    <template v-for="header in lpEntryHeaders.filter(h => h.field === 'velocity')" v-slot:[`item.${header.key}`]="{ item }">

      <template v-for="{lpEntry} of lp(header.lpId, item.date)">
        <span>
          {{ lpEntry.velocity?.get(context.velocityWindow) }}
          <v-overlay activator="parent" location="right" location-strategy="connected" open-on-hover :scrim="false">
            <v-sheet class="border-thin">
              <v-table density="compact">
                <thead>
                  <tr>
                    <th scope="col">{{ $t('titles.duration') }}</th>
                    <th scope="col">{{ $t('titles.count') }}</th>
                    <th scope="col">{{ $t('titles.count_over_24') }}</th>
                  </tr>
                </thead>
                <tbody class="text-end">
                  <tr v-for="[hours, count] in [12, 24, 72].map(h => [h, lpEntry.velocity?.get(h)])">
                    <td>{{ $t('titles.hour_count_short', hours!) }}</td>
                    <td>{{ count !== undefined ? count : '—' }}</td>
                    <td>{{ count !== undefined ? Math.round(count * (24/hours!)) : '—'}}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-sheet>
          </v-overlay>
        </span>

      </template>

    </template>

    <template v-for="header in lpEntryHeaders.filter(h => h.field === 'cost')" v-slot:[`item.${header.key}`]="{ item }">

      <template v-for="{lpEntry, lpContext} of lp(header.lpId, item.date)">

        <uri-daily-rates
          v-model="lpEntry.costState.channelDefaultRates"
          :context="lpContext"
          :disabled="item.disabled || busy"
        />

      </template>

    </template>


  </v-data-table>

</template>

<script setup lang="ts">

import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UriDailyRates from './UriDailyRates.vue'
import type { UriContext, UriLpContext, UriLpEntry, UriState } from './types'
import { currencyFormatter, percentageFormatter } from '../../../../utils/formatters'
import { required, integer, minValue, maxValue } from '../../../../validation'
import type { VDataTableHeader } from '../../../../types/vuetify'
import PreferencesService from '../../../../services/PreferencesService'
import { Temporal } from '@js-temporal/polyfill'
import ChannelRegistry from '../../../../channels/ChannelRegistry'

interface Props {
  context: UriContext
  state: UriState
  busy: boolean
}

const props = defineProps<Props>()

const { t } = useI18n()

defineEmits<{
  bulkEditCost: [lpId: number]
  bulkEditInventory: [lpId: number]
  editDefaultRates: [lpId: number]
}>()

/**
 * Maps of lpContexts and lpEntries, for quick access to these values.
 * I tried a couple of other approaches to this, but they're full of gotchas. Dynamic columns in Vuetify tables are annoying.
 */

const lpContextMap = computed<Map<number, UriLpContext>>(() => {
  const output = new Map()
  props.context.lps.forEach((lpContext) => {
    output.set(lpContext.lpId, lpContext)
  })
  return output
})

const lpEntryMap = computed<Map<number, Map<string, UriLpEntry>>>(() => {
  const output = new Map()
  props.context.lps.forEach((lpContext) => {
    const lpEntriesForDate = new Map<string, UriLpEntry>()
    props.state.dates.forEach((date) => {
      const lpEntry = date.lps.find(lpEntry => lpEntry.lpId === lpContext.lpId)
      if (lpEntry) {
        lpEntriesForDate.set(date.date.toString(), lpEntry)
      }
    })
    output.set(lpContext.lpId, lpEntriesForDate)
  })
  return output
})

// A sneaky way to inject cell-specific values into our VDataTable templates.
function lp(lpId: number, date: Temporal.PlainDate): {lpContext: UriLpContext, lpEntry: UriLpEntry}[] {
  const lpContext = lpContextMap.value.get(lpId)
  const lpEntry = lpEntryMap.value.get(lpId)?.get(date.toString())
  if (lpContext && lpEntry) {
    return [{lpContext, lpEntry}]
  } else {
    return []
  }
}


/**
 * Table headers
 */

type VDataTableHeaderForLp = VDataTableHeader & {lpId: number, field: string}

const headers = computed<VDataTableHeader[]>(() => {

  const output: VDataTableHeader[] = [
    {key: 'date', title: t('titles.date'), width: '1%', fixed: true},
    {key: 'total_inventory', title: t('titles.total_inventory'), width: '1%', minWidth: 200},
  ]

  props.context.lps.forEach(lpContext => {

    const prefix = `lp${lpContext.lpId}`

    const children: VDataTableHeaderForLp[] = [
      {key: `${prefix}.inventory`, title: t('titles.inventory'), headerProps: {class: 'border-s-thin', align: 'center'}, cellProps: {class: 'border-s-thin'}, width: '1%', minWidth: 320, lpId: lpContext.lpId, field: 'inventory'},
      {key: `${prefix}.velocity`, title: t('titles.velocity'), headerProps: {class: 'border-s-thin'}, width: '1%', align: 'center', lpId: lpContext.lpId, field: 'velocity'},
      {key: `${prefix}.cost`, title: t('titles.cost'), headerProps: {class: 'border-s-thin', align: 'center'}, width: '1%', minWidth: 240, lpId: lpContext.lpId, field: 'cost'},
    ]

    output.push({
      key: `lp${lpContext.lpId}`,
      title: lpContext.name,
      sortable: false,
      align: 'center',
      width: '1%',
      headerProps: {class: 'border-s-thin'},
      children: children,
    })

  })

  output.push({key: 'placeholder'}) // placeholder column to fill any remaining horizontal space

  return output

})

const lpEntryHeaders = computed<VDataTableHeaderForLp[]>(() => {

  const output: VDataTableHeaderForLp[] = []

  return headers.value.reduce((acc, h: VDataTableHeader): VDataTableHeaderForLp[] => {
    // @ts-ignore
    return [...acc, ...(h?.children ?? [])]
  }, output)

})

/**
 * Miscellaneous
 */

const rules = {
  spacesTotal: [required(), integer(), minValue(0), maxValue(props.context.locationSpacesTotal, 'Cannot exceed the location\'s Total Parking Spaces')],
}

</script>

<style lang="scss" scoped>

.allocation-input {

  .v-field {
    // TODO: constrain width (how can I target this element?)
    // border: 2px lime solid !important;
  }

  .allocation-input-prepend {
    width: 72px;
    text-align: end;
  }

  .allocation-input-append {
    width: 72px;
  }

}

//.uri-list-table {
//
//  .v-data-table__td {
//    padding-top: 8px !important;
//    padding-bottom: 8px;
//  }
//
//}

</style>
